import { Fragment, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import * as Tooltip from '@radix-ui/react-tooltip';

/**
 * AvomaTooltipV2 - Enhanced tooltip component built on Radix UI
 *
 * This component provides a more robust and accessible tooltip implementation
 * compared to the original AvomaTooltip component. Key improvements include:
 *
 * - Built on Radix UI's tooltip primitives for better accessibility
 * - Automatic collision detection and positioning
 * - Better keyboard navigation support
 * - Improved screen reader compatibility
 * - Configurable alignment and positioning
 *
 *
 * @param children - The element that triggers the tooltip
 * @param tooltip - The tooltip text to display (optional - renders children only if not provided)
 * @param align - Horizontal alignment: 'start', 'center', or 'end'
 * @param side - Vertical position: 'top' or 'bottom'
 */
interface IAvomaTooltipV2Props {
  children: ReactNode;
  tooltip?: string | ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  /** Optional class name for the tooltip content (e.g. to set width: "max-w-[240px]" or "w-64") */
  contentClassName?: string;
}

const DEFAULT_CONTENT_CLASS =
  'bg-gunmetal text-white font-bold rounded-md px-2 py-1 text-xs max-w-[168px] whitespace-wrap select-none shadow-lg z-[999999]';

const AvomaTooltipV2 = ({
  children,
  tooltip,
  align = 'center',
  side = 'top',
  sideOffset = 5,
  contentClassName
}: IAvomaTooltipV2Props) => {
  // If no tooltip is provided, just render the children
  if (!tooltip) {
    return <Fragment>{children}</Fragment>;
  }

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={300}>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className={twMerge(DEFAULT_CONTENT_CLASS, contentClassName)}
            sideOffset={sideOffset}
            collisionPadding={10}
            align={align}
            side={side}
          >
            {tooltip}
            <Tooltip.Arrow className='fill-gunmetal -mt-px origin-center scale-125' />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default AvomaTooltipV2;
